import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../constants/common'
import { SERVICE_REQUEST_EMAILS_DRAFT_PREFIX } from '../../constants/email'
import { REQUEST_PROFILE_CHANGE_TYPE } from '../../constants/request'
import { Screen, ScreenContainerRef } from '../../context'
import {
  getPageBrowserTabTitleWithEntityName,
  usePageBrowserTabTitle,
} from '../../hooks'
import { getSubSearchParam } from '../../utils/route'
import { EmailDraftProvider } from '../Email'
import { EntityPageProfileLayout } from '../Page'

import {
  QA_NAME_REQUEST_PROFILE_PAGE,
  REQUEST_PROFILE_TAB_ACTIVITIES,
  REQUEST_PROFILE_TAB_EMAILS,
} from './RequestProfile.constants'
import {
  useRequestProfileEmailsRealtime,
  useRequestProfileEntity,
  useRequestProfileListUnreadSync,
  useRequestProfileProvidersData,
  useRequestProfileRelatedRequestsCount,
  useRequestProfileReloadAfterChange,
  useRequestProfileReloadRegistries,
  useRequestProfileReloadsWithList,
} from './RequestProfile.hooks'
import { isRequestProfileTabEmails } from './RequestProfile.utils'
import { RequestProfileActionsWidget } from './RequestProfileActionsWidget'
import { RequestProfileContextProvider } from './RequestProfileContextProvider'
import {
  RequestProfileHeader,
  useRequestProfileHeaderPhases,
} from './RequestProfileHeader'
import { RequestProfileLoader } from './RequestProfileLoader'
import { RequestProfileOpenActivities } from './RequestProfileOpenActivities'
import { RequestProfileScreenContainer } from './RequestProfileScreenContainer'
import {
  REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY,
  RequestProfileTabEmailsSummaryExpandContextProvider,
  RequestProfileTabEmailsThreadModal,
  RequestProfileTabEmailsViewTypeContextProvider,
} from './RequestProfileTabEmails'
import {
  RequestProfileTabs,
  useRequestProfileTabsCounters,
} from './RequestProfileTabs'
import {
  RequestProfileWidgetList,
  useRequestProfileWidgetAISummaryData,
} from './RequestProfileWidget'

import * as styles from './RequestProfile.module.css'

function RequestProfile({
  headerProps = EMPTY_OBJECT,
  isPreview = false,
  layoutProps = EMPTY_OBJECT,
  outboundUuid,
  profileLoadersRef,
  requestId: requestIdProp,
  onReloadList,
  onReloadListExtra,
  onSuccessMarkAsUnread,
  onSyncListItemUnread,
}) {
  const { requestId: requestIdFromParams, tab: tabFromUrl } = useParams()

  const requestId = requestIdProp ?? requestIdFromParams

  const [searchParams] = useSearchParams()

  const sub = isPreview ? null : getSubSearchParam(searchParams)

  const { t } = useTranslation()

  const { isMobileScreenSize } = useContext(Screen)

  const [modal, setModal] = useState(null)
  const [isPerformingAction, setPerformingAction] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  const openedInitialThreadKeyRef = useRef(null)

  function handleCloseModal() {
    setModal(null)
  }

  const {
    handleEntityReload,
    handleMergeEntityData,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    requestData,
    requestTitle,
    serverError,
  } = useRequestProfileEntity({ outboundUuid, requestId, onReloadList })

  const { handleLoadRequestProviders, requestProviders } =
    useRequestProfileProvidersData({
      outboundUuid,
      requestData,
    })

  const {
    patchSummaryData: patchRequestSummaryData,
    summaryData: requestSummaryData,
  } = useRequestProfileWidgetAISummaryData({
    isEnabled: !outboundUuid,
    requestData,
  })

  const { handleLoadRelatedRequestsCount, relatedRequestsCount } =
    useRequestProfileRelatedRequestsCount({
      canViewRelatedRequests:
        requestData?.permissions?.can_view_related_requests,
      isArchived: requestData?.isArchived,
      requestId,
    })

  const { arePhasesLoading, handleReloadRequestPhases, phases } =
    useRequestProfileHeaderPhases({
      outboundUuid,
      requestId,
    })

  const { counters, handleLoadCounters, isCountersReady } =
    useRequestProfileTabsCounters({
      outboundUuid,
      requestId: requestData?.id,
    })

  useRequestProfileListUnreadSync({
    isEnabled: isPreview && isInitiated && requestData != null,
    requestId,
    unread: Boolean(requestData?.unread),
    onSyncListItemUnread,
  })

  const {
    handleReloadActivitiesTab,
    handleReloadDocFlowsTab,
    handleReloadDocumentsTab,
    handleReloadEmailsTab,
    handleReloadOpenActivities,
    handleReloadPostsTab,
    handleReloadRating,
    handleReloadRelatedRequests,
    registerActivitiesTabReload,
    registerDocFlowsTabReload,
    registerDocumentsTabReload,
    registerEmailsTabReload,
    registerOpenActivitiesReload,
    registerPostsTabReload,
    registerRatingReload,
    registerRelatedRequestsReload,
  } = useRequestProfileReloadRegistries()

  const { registerEmailsRealtimeListener } = useRequestProfileEmailsRealtime({
    channel: requestData?.realtime_channels?.emails,
    enabled: isPreview || isRequestProfileTabEmails(tabFromUrl),
    onReloadListExtra,
  })

  const { handleReloadAfterChange } = useRequestProfileReloadAfterChange({
    onLoadCounters: handleLoadCounters,
    onLoadRelatedRequestsCount: handleLoadRelatedRequestsCount,
    onLoadRequestProviders: handleLoadRequestProviders,
    onReloadActivitiesTab: handleReloadActivitiesTab,
    onReloadDocFlowsTab: handleReloadDocFlowsTab,
    onReloadDocumentsTab: handleReloadDocumentsTab,
    onReloadEmailsTab: handleReloadEmailsTab,
    onReloadEntity: handleEntityReload,
    onReloadList,
    onReloadOpenActivities: handleReloadOpenActivities,
    onReloadPostsTab: handleReloadPostsTab,
    onReloadRating: handleReloadRating,
    onReloadRelatedRequests: handleReloadRelatedRequests,
    onReloadRequestPhases: handleReloadRequestPhases,
  })

  const shouldLoadOpenActivities = Boolean(
    requestData?.permissions?.can_view_open_activities &&
    counters[REQUEST_PROFILE_TAB_ACTIVITIES]?.total
  )

  const {
    handleEntityReloadWithList,
    handleLoadCountersWithList,
    handleReloadOpenActivitiesWithList,
  } = useRequestProfileReloadsWithList({
    onEntityReload: handleEntityReload,
    onLoadCounters: handleLoadCounters,
    onReloadList,
    onReloadListExtra,
    onReloadOpenActivities: handleReloadOpenActivities,
  })

  const initialThreadUuid = searchParams.get(
    REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY
  )

  function handleInitialThreadRead() {
    handleLoadCountersWithList([REQUEST_PROFILE_TAB_EMAILS])
  }

  async function handleReloadInitialThreadChanges() {
    return handleReloadAfterChange(REQUEST_PROFILE_CHANGE_TYPE.emails)
  }

  // i2i requests hide the Emails tab, so a deep-linked thread opens here with
  // the whole thread list as context. Keyed on request + thread: a new deep
  // link reopens / remounts the modal, closing the current thread does not.
  useEffect(() => {
    const threadKey =
      outboundUuid && initialThreadUuid && requestId
        ? `${requestId}:${initialThreadUuid}`
        : null

    if (threadKey && openedInitialThreadKeyRef.current !== threadKey) {
      openedInitialThreadKeyRef.current = threadKey

      setModal(
        <RequestProfileTabEmailsThreadModal
          key={threadKey}
          mailUuid={initialThreadUuid}
          navigationContext={{
            filterValues: EMPTY_OBJECT,
            ordering: EMPTY_LIST,
            requestId,
          }}
          requestId={requestId}
          onClose={handleCloseModal}
          onReload={handleReloadInitialThreadChanges}
          onSuccessRead={handleInitialThreadRead}
        />
      )
    }
  }, [initialThreadUuid, outboundUuid, requestId])

  useEffect(() => {
    async function handleReloadProfile() {
      return handleReloadAfterChange(REQUEST_PROFILE_CHANGE_TYPE.external)
    }

    if (profileLoadersRef) {
      profileLoadersRef.current = { handleReloadProfile }

      return () => {
        profileLoadersRef.current = null
      }
    }
  }, [handleReloadAfterChange, profileLoadersRef])

  usePageBrowserTabTitle({
    isEnabled: !isPreview,
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: requestTitle,
      title: t('BrowserTab.RequestProfileTabTitleKey'),
    }),
  })

  return (
    <RequestProfileScreenContainer
      isPreview={isPreview}
      scrollWrapperRef={pageScrollWrapperRef}
    >
      <RequestProfileContextProvider
        arePhasesLoading={arePhasesLoading}
        handleCloseModal={handleCloseModal}
        handleEntityReload={handleEntityReloadWithList}
        handleLoadCounters={handleLoadCountersWithList}
        handleLoadRelatedRequestsCount={handleLoadRelatedRequestsCount}
        handleMergeEntityData={handleMergeEntityData}
        handleReloadActivitiesTab={handleReloadActivitiesTab}
        handleReloadAfterChange={handleReloadAfterChange}
        handleReloadOpenActivities={handleReloadOpenActivitiesWithList}
        handleReloadRequestPhases={handleReloadRequestPhases}
        handleUpdateEntity={handleUpdateEntity}
        isInitiated={isInitiated}
        isLoading={isLoading}
        isPerformingAction={isPerformingAction}
        isPreview={isPreview}
        outboundUuid={outboundUuid}
        patchRequestSummaryData={patchRequestSummaryData}
        phases={phases}
        registerActivitiesTabReload={registerActivitiesTabReload}
        registerDocFlowsTabReload={registerDocFlowsTabReload}
        registerDocumentsTabReload={registerDocumentsTabReload}
        registerEmailsRealtimeListener={registerEmailsRealtimeListener}
        registerEmailsTabReload={registerEmailsTabReload}
        registerOpenActivitiesReload={registerOpenActivitiesReload}
        registerPostsTabReload={registerPostsTabReload}
        registerRatingReload={registerRatingReload}
        registerRelatedRequestsReload={registerRelatedRequestsReload}
        relatedRequestsCount={relatedRequestsCount}
        requestContractors={requestProviders.contractors}
        requestData={requestData}
        requestId={requestId}
        requestProvidersPermissions={requestProviders.permissions}
        requestSideWorkers={requestProviders.workers}
        requestSummaryData={requestSummaryData}
        scrollWrapperRef={pageScrollWrapperRef}
        setModal={setModal}
        setPerformingAction={setPerformingAction}
        sub={sub}
        onSuccessMarkAsUnread={onSuccessMarkAsUnread}
      >
        <EmailDraftProvider
          prefix={`${SERVICE_REQUEST_EMAILS_DRAFT_PREFIX}-${requestId}`}
        >
          <RequestProfileTabEmailsSummaryExpandContextProvider>
            <RequestProfileTabEmailsViewTypeContextProvider key={requestId}>
              <EntityPageProfileLayout
                {...layoutProps}
                Header={RequestProfileHeader}
                Loader={RequestProfileLoader}
                actionsWidget={
                  isPreview || isMobileScreenSize ? null : (
                    <RequestProfileActionsWidget isSkeleton={!isInitiated} />
                  )
                }
                contentClassName={styles.content}
                headerProps={{
                  ...headerProps,
                  requestTitle,
                  stickyTitleClassName: styles.stickyTitle,
                }}
                isEmbedded={isPreview}
                qaName={QA_NAME_REQUEST_PROFILE_PAGE}
                scrollWrapperRef={pageScrollWrapperRef}
                serverError={serverError}
                widgets={<RequestProfileWidgetList />}
              >
                {shouldLoadOpenActivities ? (
                  <RequestProfileOpenActivities
                    scrollContainerRef={pageScrollWrapperRef}
                    onReloadCounters={handleLoadCountersWithList}
                  />
                ) : null}
                <RequestProfileTabs
                  counters={counters}
                  handleLoadCounters={handleLoadCountersWithList}
                  isCountersReady={isCountersReady}
                  requestData={requestData}
                />
                <ScreenContainerRef value={null}>{modal}</ScreenContainerRef>
              </EntityPageProfileLayout>
            </RequestProfileTabEmailsViewTypeContextProvider>
          </RequestProfileTabEmailsSummaryExpandContextProvider>
        </EmailDraftProvider>
      </RequestProfileContextProvider>
    </RequestProfileScreenContainer>
  )
}

export default RequestProfile
